/**
 * Browser-local onboarding preferences — ADR-0167 (amended 2026-07-07:
 * the welcome-step platform question was removed — detect-platform is
 * authoritative; never ask what we can detect — so the platform-choice
 * surface is gone).
 *
 * Two tiny persistence surfaces, both `localStorage`-backed and both
 * best-effort (private-mode / quota failures degrade to in-memory
 * defaults, never throw):
 *
 *   - `welcomed`         — the one-screen welcome has been dismissed.
 *   - `guide dismissed`  — the SitesList setup-guide card was hidden.
 *
 * Browser-local by design (v1): a second device re-shows the welcome
 * once. Server-side user preferences are a recorded follow-up in the
 * ADR — do not grow this file into one.
 *
 * `shouldShowWelcome` is pure and exported for unit tests.
 */

const WELCOME_KEY = 'sill:onboarding_welcomed';
const GUIDE_DISMISSED_KEY = 'sill:setup_guide_dismissed';

function safeGet(key: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* best-effort — private mode / quota */
  }
}

export function wasWelcomed(): boolean {
  return safeGet(WELCOME_KEY) === '1';
}

export function markWelcomed(): void {
  safeSet(WELCOME_KEY, '1');
}

export function isSetupGuideDismissed(): boolean {
  return safeGet(GUIDE_DISMISSED_KEY) === '1';
}

export function dismissSetupGuide(): void {
  safeSet(GUIDE_DISMISSED_KEY, '1');
}

/**
 * ADR-0167 §1 — the welcome renders ONLY on the new-account entry path:
 *
 *   - `pathname` starts with `/onboarding` — the `auth.ts` verify
 *     redirect target for `is_new_account` sign-ins. In-app navigation
 *     (`setView`) writes `?view=` and never rewrites the pathname, so a
 *     returning merchant clicking "Add site" from `/` never matches.
 *   - NO `?connected=` param — a Shopify OAuth return must mount the
 *     install step's advance effect, never the welcome (ADR-0053 §1.1).
 *   - NO `?view=` param — a refresh mid-session is navigation intent,
 *     mirroring `initialView`'s precedence rule.
 *   - the browser-local `welcomed` flag is unset.
 *
 * Pure over its inputs; the caller supplies `location` + the flag so
 * tests exercise the real gate.
 */
export function shouldShowWelcome(
  location: { pathname: string; search: string },
  welcomed: boolean,
): boolean {
  if (welcomed) return false;
  if (!location.pathname.startsWith('/onboarding')) return false;
  const params = new URLSearchParams(location.search);
  if (params.get('connected') !== null) return false;
  if (params.get('view') !== null) return false;
  return true;
}
